<template>
    <div :class="{ 'edit-mode': editing, 'read-mode': !editing }">
        <div class='header-container mt-3 mb-3'>
            <v-card outlined class='w-auto'>
                <div class='w-auto d-flex flex-row align-center pa-2'>
                    <v-select
                        dense
                        v-model='filterEquipment'
                        :items='filterOptions.equipments'
                        label='Equipment'
                        outlined
                        hide-details
                        item-text='value'
                        class='filter-options mr-2'
                        return-object
                    >
                    </v-select>
                    <v-select
                        dense
                        v-model='filterLocation'
                        :items='filterOptions.locations'
                        label='Location'
                        outlined
                        hide-details
                        item-text='value'
                        class='filter-options mr-2'
                        return-object
                    >
                    </v-select>
                    <v-select
                        dense
                        v-model='filterTaskType'
                        :items='filterOptions.taskTypes'
                        label='Task Type'
                        outlined
                        hide-details
                        item-text='value'
                        class='filter-options mr-2'
                        return-object
                    >
                    </v-select>
                    <v-select
                        dense
                        v-model='filterOperator'
                        :items='filterOptions.operators'
                        label='Operator'
                        outlined
                        hide-details
                        item-text='value'
                        class='filter-options mr-2'
                        return-object
                    >
                    </v-select>
                    <v-btn class='filter-button mr-2'
                           outlined
                           @click='loadData(true)'
                           color='primary'>
                        <span v-if='!filterRefresh'>Filter</span>
                        <v-progress-circular v-else indeterminate :size='20' />
                    </v-btn>
                    <v-btn
                        outlined
                        class='clear-filter '
                        @click='clearFilter()'
                        color='primary'>
                        <v-icon class='pa-0 ma-0'>mdi-close</v-icon>
                    </v-btn>
                </div>
            </v-card>
            <div class='d-flex flex-row'>
                <v-switch
                    v-model='showDelays'
                    label='Show Delays'
                    dense
                    hide-details
                    class='my-0 mr-2'
                    key="showDelaysSwitch"
                ></v-switch>
                <v-chip
                    v-if='editing && pageStatus'
                    class='status-chips mr-2'
                    outlined
                    :color="pageStatus === 'Saved' ? 'green' : 'orange'"
                    key="pageStatusChip"
                >
                    {{ pageStatus }}
                </v-chip>
                <v-tooltip v-if='displaySyncTimesButton' bottom>
                    <template v-slot:activator='{ on }'>
                        <v-btn
                            :disabled='saving'
                            @click='syncTimes'
                            color='primary'
                            outlined
                            class='ml-2 '
                            v-on="on"
                        >
                            Sync Times
                        </v-btn>
                    </template>
                    <span>
                    Pull start and end times for entries from the shift plan.
                  </span>
                </v-tooltip>

                <v-btn
                    v-if='editing'
                    :disabled='saving'
                    @click='addRow()'
                    color='primary'
                    outlined
                    class='ml-2 '>
                    Add Plod Entry
                </v-btn>
                <v-btn
                    v-if='areReportable'
                    @click='cancelReportability'
                    color='primary'
                    class='ml-2 '
                    key="markNotReportableButton"
                >
                    Undo Completion
                </v-btn>
                <v-btn
                    v-if='!areReportable'
                    @click='markReportable'
                    color='primary'
                    class='ml-2 '
                    key="markReportableButton"
                >
                    Complete
                </v-btn>
            </div>
        </div>

        <div v-if='dataLoaded && formData.actualsRows.length === 0'
             class='grey lighten-4 pa-5 text-center'>
            No actuals data found matching this shift and filter criteria.
            <div class='mt-5' v-if='editing && filterOptions.taskTypes.length === 1'>
                <v-btn
                    @click='populateShiftData()'
                    color='primary'
                    style='text-align: center'>
                    Populate Data For Shift
                </v-btn>
            </div>
        </div>

        <div v-if='!dataLoaded' class='grey lighten-4 pa-5 text-center'>
            <v-progress-circular
                indeterminate
                color='primary'
            ></v-progress-circular>
        </div>

        <div v-if='dataLoaded && formData.actualsRows.length > 0'
             :class="{ 'high-level-actuals': activeFilteredLocation }" class='table_wrapper ml-15 mr-15'>

            <v-data-table
                :items='filteredAndGroupedActuals'
                :hide-default-footer='true'
                class='custom-plod-table'
                hide-default-header
                :disable-pagination='true'
                :key='tableKey'
                :loading='!dataLoaded'
            >
                <!-- Dynamically generating the headers -->
                <template v-slot:header>
                    <thead>
                    <tr class='header-s'>
                        <th rowspan='2' class='indicator-column header'></th>
                        <th rowspan='2' class='actions-column header'></th>
                        <th rowspan='2' class='extra-wide header'>
                            <div class='sortable-field' @click="changeSorting(CONSTANTS.FIELDS.LOCATION)">
                                <v-icon :color="editing ? 'white' : 'black'" v-if='activeFilteredLocation'>mdi-filter
                                </v-icon>
                                Location
                                <v-icon :color="editing ? 'white' : 'black'" v-if="sortColumn === CONSTANTS.FIELDS.LOCATION">
                                    mdi-arrow-{{ sortDirection }}
                                </v-icon>
                            </div>
                        </th>
                        <th rowspan='2' class='extra-wide header' v-if="displayBlastPacketsAndRings">
                            <div>
                                Blast Packet
                            </div>
                        </th>
                        <th rowspan='2' class='extra-wide header' v-if="displayBlastPacketsAndRings">
                            <div>
                                Ring
                            </div>
                        </th>
                        <th rowspan='2' class='extra-wide header'>
                            <div class='sortable-field' @click="changeSorting(CONSTANTS.FIELDS.TASK_TYPE)">
                                <v-icon :color="editing ? 'white' : 'black'" v-if='activeFilteredTaskType'>mdi-filter
                                </v-icon>
                                Task (Type)
                                <v-icon :color="editing ? 'white' : 'black'" v-if="sortColumn === CONSTANTS.FIELDS.TASK_TYPE">
                                    mdi-arrow-{{ sortDirection }}
                                </v-icon>
                            </div>
                        </th>
                        <th rowspan='2' class='extra-wide header'>
                            <div class='sortable-field' @click="changeSorting(CONSTANTS.FIELDS.EQUIPMENT)">
                                <v-icon :color="editing ? 'white' : 'black'" v-if='activeFilteredEquipment'>mdi-filter
                                </v-icon>
                                {{ 'Equipment' | termPlural }}
                                <v-icon :color="editing ? 'white' : 'black'" v-if="sortColumn === CONSTANTS.FIELDS.EQUIPMENT">
                                    mdi-arrow-{{ sortDirection }}
                                </v-icon>
                            </div>
                        </th>
                        <th rowspan='2' class='extra-wide header'>
                            <div class='sortable-field' @click="changeSorting(CONSTANTS.FIELDS.OPERATOR)">
                                <v-icon :color="editing ? 'white' : 'black'" v-if='activeFilteredOperator'>mdi-filter
                                </v-icon>
                                Operator
                                <v-icon :color="editing ? 'white' : 'black'" v-if="sortColumn === CONSTANTS.FIELDS.OPERATOR">
                                    mdi-arrow-{{ sortDirection }}
                                </v-icon>
                            </div>
                        </th>
                        <th rowspan='2' class='extra-wide'>Comments</th>
                        <th rowspan='2' class='col-time'>Start Time</th>
                        <th rowspan='2' class='col-time'>End Time</th>
                        <th v-if='editing' rowspan='2' class='ignore-actuals-column'>Ignore</th>
                        <th class='group-name' v-for='headerGroup in nonArchivedGroups' :key='headerGroup.groupId'
                            :colspan='headerGroup.definitions.length'>
                            {{ headerGroup.groupName }}
                        </th>
                    </tr>
                    <tr class='header-s'>
                        <template v-for='headerGroup in nonArchivedGroups'>
                            <th v-for='headerGroupItem in headerGroup.definitions' class='subheader'
                                :class='`col-${headerGroupItem.definitionType.toLowerCase()}`'
                                :key='`${headerGroup.groupId}-${headerGroupItem.id}`'>
                                {{ headerGroupItem.definitionName }}
                                <div
                                    v-if="headerGroupItem.definitionType === 'Formula' && headerGroupItem.formula">
                                    <v-tooltip bottom>
                                        <template v-slot:activator='{ on, attrs }'>
                    <span
                        v-bind='attrs'
                        v-on='on' class='formula-indicator'
                    >Formula</span>
                                        </template>
                                        <span>
                    {{ headerGroupItem.formulaDescription }}
                  </span>
                                    </v-tooltip>
                                </div>
                            </th>
                        </template>
                    </tr>
                    </thead>
                </template>

                <!-- Body of the table -->
                <template v-slot:body='{ items }'>
                    <tbody class='tbody'>
                    <template v-for='item in items'>
                        <tr :key='`${item.id}`' :class="{'grouped-row-child': item.hierarchyType === CONSTANTS.ROW_HIERARCHY.CHILD && displayParentRows }">
                            <td>
                                <v-tooltip bottom
                                           v-if="item.hierarchyType !== CONSTANTS.ROW_HIERARCHY.PARENT && (item.status === CONSTANTS.STATUS.ERROR || (!item.taskTypeId && (!item.taskType || !item.taskType.id)))">
                                    <template v-slot:activator='{ on, attrs }'>
                                        <v-icon color='warning'
                                                v-bind='attrs'
                                                v-on='on'>mdi-alert-circle
                                        </v-icon>
                                    </template>
                                    <span>Row will not save until Task Type is selected.</span>
                                </v-tooltip>
                                <v-tooltip bottom
                                           v-if="item.hierarchyType === CONSTANTS.ROW_HIERARCHY.PARENT">
                                    <template v-slot:activator='{ on, attrs }'>
                                        <v-icon color='grey'
                                                v-bind='attrs'
                                                v-on='on'>mdi-information-outline
                                        </v-icon>
                                    </template>
                                    <span>Represents actuals all associated with the same piece of work.</span>
                                </v-tooltip>

                            </td>
                            <td>
                                <div v-if="editing" class="d-flex flex-column justify-center">
                                    <v-btn icon color='red' @click='deleteActuals(item)' v-if="item.hierarchyType !== CONSTANTS.ROW_HIERARCHY.PARENT">
                                        <v-icon>mdi-delete</v-icon>
                                    </v-btn>
                                    <v-tooltip bottom
                                               v-if="item.hierarchyType === CONSTANTS.ROW_HIERARCHY.PARENT">
                                        <template v-slot:activator='{ on }'>
                                            <v-btn icon color="primary" @click='addChildRow(item)' v-on="on" >
                                                <v-icon>mdi-plus</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Add a plod entry for this piece of work.</span>
                                    </v-tooltip>
                                </div>
                            </td>
                            <td>
                                <v-combobox
                                    v-if="!displayParentRows || item.hierarchyType !== CONSTANTS.ROW_HIERARCHY.CHILD"
                                    v-model='item.location'
                                    dense
                                    outlined
                                    :items='locationList'
                                    hide-details
                                    class='small-input fixed-width-combo'
                                    item-text='name'
                                    item-value='id'
                                    :return-object='true'
                                    height="30"
                                    :readonly='!editing || uiLocked || !!filterLocation.key'
                                    @input="handleInputChange(item, null, null, CONSTANTS.FIELDS.LOCATION)"
                                ></v-combobox>
                                <v-text-field
                                    v-if='(!displayParentRows || item.hierarchyType !== CONSTANTS.ROW_HIERARCHY.CHILD) && item.locationDisplayName'
                                    class='small-input mt-3 fixed-width-combo'
                                    v-model='item.locationDisplayName'
                                    hide-details
                                    label='Display Name'
                                    outlined
                                    persistent-placeholder
                                    dense
                                    readonly
                                ></v-text-field>
                            </td>
                            <td v-if="displayBlastPacketsAndRings">
                                <v-select
                                    v-if="!displayParentRows || item.hierarchyType !== CONSTANTS.ROW_HIERARCHY.CHILD"
                                    v-model='item.blastPacket'
                                    dense
                                    outlined
                                    :items='getPermittedBlastPacketsForLocation(item.location)'
                                    clearable
                                    hide-details
                                    class='small-input fixed-width-combo'
                                    item-text='name'
                                    item-value='id'
                                    :return-object='true'
                                    :readonly='!editing || uiLocked'
                                    @input="handleInputChange(item, null, null, CONSTANTS.FIELDS.BLAST_PACKET)"
                                >
                                </v-select>
                            </td>
                            <td v-if="displayBlastPacketsAndRings">
                                <v-select
                                    v-if="!displayParentRows || item.hierarchyType !== CONSTANTS.ROW_HIERARCHY.CHILD"
                                    v-model='item.ring'
                                    dense
                                    outlined
                                    :items='getPermittedRingsForLocation(item.location, item.blastPacket)'
                                    clearable
                                    hide-details
                                    class='small-input fixed-width-combo'
                                    item-text='name'
                                    item-value='id'
                                    :return-object='true'
                                    :readonly='!editing || uiLocked'
                                    @input="handleInputChange(item, null, null, CONSTANTS.FIELDS.RING)"
                                >
                                </v-select>
                            </td>
                            <td>
                                <v-select
                                    v-if="!displayParentRows || item.hierarchyType !== CONSTANTS.ROW_HIERARCHY.CHILD"
                                    v-model='item.taskType'
                                    dense
                                    outlined
                                    :items='permittedTaskTypeList'
                                    hide-details
                                    class='small-input fixed-width-combo'
                                    item-text='name'
                                    item-value='id'
                                    :return-object='true'
                                    :readonly='!editing || uiLocked || !!filterTaskType.key || !item.isCustom'
                                    @input="handleInputChange(item, null, null, CONSTANTS.FIELDS.TASK_TYPE)"
                                >
                                    <template v-if='!item.isCustom && editing' v-slot:append>
                                        <v-icon class='lock-icon'>mdi-lock</v-icon>
                                    </template>
                                </v-select>
                                <v-text-field
                                    v-if='(!displayParentRows || item.hierarchyType !== CONSTANTS.ROW_HIERARCHY.CHILD) && item.taskTypeDisplayName'
                                    class='small-input mt-3 fixed-width-combo'
                                    v-model='item.taskTypeDisplayName'
                                    hide-details
                                    label='Display Name'
                                    outlined
                                    persistent-placeholder
                                    dense
                                    readonly
                                ></v-text-field>
                            </td>
                            <td>
                                <v-select
                                    v-if="item.hierarchyType !== CONSTANTS.ROW_HIERARCHY.PARENT"
                                    v-model='item.equipment'
                                    dense
                                    outlined
                                    :items='equipmentList'
                                    hide-details
                                    class='small-input fixed-width-combo'
                                    item-text='name'
                                    item-value='id'
                                    :return-object='true'
                                    :readonly='!editing || uiLocked || !!filterEquipment.key'
                                    @input="handleInputChange(item, null, null, CONSTANTS.FIELDS.EQUIPMENT)"
                                ></v-select>
                            </td>
                            <td>
                                <v-select
                                    v-if="item.hierarchyType !== CONSTANTS.ROW_HIERARCHY.PARENT"
                                    v-model='item.operator'
                                    dense
                                    outlined
                                    :items='operatorsList'
                                    hide-details
                                    class='small-input fixed-width-combo'
                                    item-text='name'
                                    item-value='id'
                                    :return-object='true'
                                    :readonly='!editing || uiLocked || !!filterOperator.key'
                                    @input="handleInputChange(item, null, null, CONSTANTS.FIELDS.OPERATOR)"
                                ></v-select>
                            </td>
                            <td>
                                <v-textarea
                                    v-if="item.hierarchyType !== CONSTANTS.ROW_HIERARCHY.PARENT"
                                    class='small-input'
                                    v-model='item.comment'
                                    outlined
                                    hide-details
                                    single-line
                                    dense
                                    rows='1'
                                    placeholder='Enter comments here...'
                                    :readonly='!editing || uiLocked'
                                    @input='handleInputChange(item)'
                                ></v-textarea>
                            </td>
                            <td>
                                <v-text-field
                                    v-if="item.hierarchyType !== CONSTANTS.ROW_HIERARCHY.PARENT"
                                    class='small-input'
                                    v-model='item.startTime'
                                    hide-details
                                    outlined
                                    dense
                                    :readonly='!editing || uiLocked'
                                    type='time'
                                    @input='handleInputChange(item)'
                                ></v-text-field>
                            </td>
                            <td>
                                <v-text-field
                                    v-if="item.hierarchyType !== CONSTANTS.ROW_HIERARCHY.PARENT"
                                    class='small-input'
                                    v-model='item.endTime'
                                    hide-details
                                    outlined
                                    dense
                                    :readonly='!editing || uiLocked'
                                    type='time'
                                    @input='handleInputChange(item)'
                                ></v-text-field>
                            </td>
                            <td>
                                <v-tooltip bottom
                                           v-if="item.hierarchyType !== CONSTANTS.ROW_HIERARCHY.PARENT">
                                    <template v-slot:activator='{ on }'>
                                        <span v-on="on">
                                            <v-checkbox
                                                v-model='item.ignoreValuesForReporting'
                                                class='small-input'
                                                hide-details
                                                dense
                                                :disabled='!editing || uiLocked'
                                                @change='handleInputChange(item, null, null, CONSTANTS.FIELDS.IGNORE_ACTUALS)'
                                            ></v-checkbox>
                                        </span>

                                    </template>
                                    <span>Ignore this row for reporting (use for entering duplicate data).</span>
                                </v-tooltip>

                            </td>

                            <template v-for='headerGroup in nonArchivedGroups'>
                                <td v-for='headerGroupItem in headerGroup.definitions'
                                    :key='`${headerGroup.groupId}-${headerGroupItem.id}`'>
                                    <v-text-field
                                        v-if="headerGroupItem.definitionType !== 'Boolean' && !valueSets[headerGroupItem.id] && item.taskType && groupIsRelevantToTaskType(headerGroup, item.taskType.id)"
                                        class='small-input'
                                        v-model="item.actualsPairs[getActualsPairKey(headerGroup.groupId, headerGroupItem.id)]"
                                        hide-details
                                        outlined
                                        dense
                                        :disabled='(item.hierarchyType === CONSTANTS.ROW_HIERARCHY.PARENT && !headerGroupItem.summable) || !item.taskType || !groupIsRelevantToTaskType(headerGroup, item.taskType.id)'
                                        :readonly="item.hierarchyType === CONSTANTS.ROW_HIERARCHY.PARENT || !editing || uiLocked || headerGroupItem.definitionType === 'Formula'"
                                        :type='getTextFieldType(headerGroupItem.definitionType)'
                                        v-on='{ keypress: (e) => validateInput(e, headerGroupItem.definitionType) }'
                                        @input='handleInputChange(item, headerGroupItem, headerGroup)'
                                    ></v-text-field>
                                    <v-select
                                        v-if='valueSets[headerGroupItem.id]  && item.taskType && groupIsRelevantToTaskType(headerGroup, item.taskType.id)'
                                        v-model="item.actualsPairs[getActualsPairKey(headerGroup.groupId, headerGroupItem.id)]"
                                        dense
                                        outlined
                                        :disabled='item.hierarchyType === CONSTANTS.ROW_HIERARCHY.PARENT || !item.taskType || !groupIsRelevantToTaskType(headerGroup, item.taskType.id)'
                                        :items='[...valueSets[headerGroupItem.id], item.actualsPairs[getActualsPairKey(headerGroup.groupId, headerGroupItem.id)]]'
                                        hide-details
                                        class='small-input'
                                        :readonly='!editing || uiLocked'
                                        @input='handleInputChange(item, headerGroupItem, headerGroup)'
                                    >
                                    </v-select>
                                    <v-checkbox
                                        v-if="headerGroupItem.definitionType === 'Boolean' && item.taskType && groupIsRelevantToTaskType(headerGroup, item.taskType.id)"
                                        v-model="item.actualsPairs[getActualsPairKey(headerGroup.groupId, headerGroupItem.id)]"
                                        class='small-input'
                                        hide-details
                                        :disabled='item.hierarchyType === CONSTANTS.ROW_HIERARCHY.PARENT || !item.taskType || !groupIsRelevantToTaskType(headerGroup, item.taskType.id)'
                                        :readonly='!editing || uiLocked'
                                        @change='handleInputChange(item, headerGroupItem, headerGroup)'
                                    ></v-checkbox>
                                </td>
                            </template>
                        </tr>
                    </template>
                    <tr v-if='nonArchivedGroups && nonArchivedGroups.length > 0' class='totals-row'>
                        <td></td>
                        <td></td>
                        <td>
                            <div style='height: 40px; font-weight: bold; align-content: center'>Totals</div>
                        </td>
                        <td v-if="displayBlastPacketsAndRings">
                        </td>
                        <td v-if="displayBlastPacketsAndRings">
                        </td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <template v-for='headerGroup in nonArchivedGroups'>
                            <td v-for='headerGroupItem in headerGroup.definitions'
                                :key='`${headerGroup.groupId}-${headerGroupItem.id}`'>
                                <v-text-field
                                    :disabled='!headerGroupItem.summable'
                                    v-model='totals[`${headerGroup.groupId}_${headerGroupItem.id}`]'
                                    class='small-input'
                                    hide-details
                                    outlined
                                    dense
                                    readonly
                                >
                                </v-text-field>
                            </td>
                        </template>
                    </tr>

                    </tbody>
                </template>
            </v-data-table>
        </div>
        <shift-printer
            :shiftId="shiftId"
            :departmentId="departmentId"
            :departmentName="departmentName"
            :department-features="departmentFeatures"
            :date="$route.params.date"
            :shift="$route.params.shift"
        ></shift-printer>
        <confirm ref='confirm'></confirm>
    </div>
</template>

<script>
import Vue from 'vue';
import Equipment from '@/lib/data/Equipment';
import Actuals from '@/lib/data/Actuals';
import { useSystemStore } from '@/lib/stores/SystemStore';
import _, { update } from 'lodash';
import { mapState } from 'pinia';
import { useShiftDetails } from '@/lib/stores/Shift';
import { v4 as uuidv4 } from 'uuid';
import { DateFormat } from '@/plugins/dates';
import { useDepartmentStore } from '@/lib/stores/DepartmentStore';
import { EventBus, Events } from '@/lib/EventBus';
import Logging from '@/lib/Logging';
import dayjs from 'dayjs';
import { useShiftWindowActuals } from '@/lib/stores/Production/ShiftWindowActualsStore';
import { CycleType, MiningMethodType } from '@/models/api';
import { RemoveLocationNameFromBlastPacketOrRingName } from '@/lib/services/Production';
import { useBlastPacketsStore } from '@/lib/stores/Production/BlastPacketsStore';
import DailyReports from '@/lib/data/DailyReports';

function groupIsRelevantToTaskType(group, taskTypeId) {
    return group.taskTypes.some(tt => tt.taskTypeId === taskTypeId);
}

function definitionCanBeSummed(definition) {
    return definition.summable && (definition.definitionType === 'Decimal' || definition.definitionType === 'Integer' || definition.definitionType === 'Formula');
}

function getHierarchySortOrder(row) {
    if (row.hierarchyType === CONSTANTS.ROW_HIERARCHY.PARENT) {
        return 0;
    } else if (row.hierarchyType === CONSTANTS.ROW_HIERARCHY.CHILD) {
        return 1;
    } else if (row.hierarchyType === CONSTANTS.ROW_HIERARCHY.INDIV) {
        return 2;
    }
    return 3;
}

function getRowOriginatingTaskIdForSorting(row) {
    return row.hierarchyType === CONSTANTS.ROW_HIERARCHY.PARENT ? row.id : row.originatingTaskId ?? '';
}

const CONSTANTS = {
    STATUS: {
        NOT_SAVED: 'Not Saved',
        ERROR: 'Error',
        SAVING: 'Saving...',
        SAVED: 'Saved'
    },
    SORT: {
        UP: 'up',
        DOWN: 'down'
    },
    FIELDS: {
        LOCATION: 'location',
        TASK_TYPE: 'taskType',
        OPERATOR: 'operator',
        EQUIPMENT: 'equipment',
        BLAST_PACKET: 'blastPacket',
        RING: 'ring',
        IGNORE_ACTUALS: 'ignoreActuals'
    },
    ROW_HIERARCHY: {
        CHILD: 'child',
        PARENT: 'parent',
        INDIV: 'individual'
    }
}

export default Vue.extend({
    props: ['editing', 'refreshRequiredToggle'],
    computed: {
        ...mapState(useDepartmentStore, ['departmentName', 'departmentFeatures', 'departmentId', 'locations', 'locationName', 'taskTypes']),
        ...mapState(useShiftWindowActuals, ['getBlastPacketsInformation']),
        ...mapState(useBlastPacketsStore, ['blastPackets', 'getFlattenedBlastPackets']),
        ...mapState(useSystemStore, ['staffMembers']),
        ...mapState(useShiftDetails, ['shiftId', 'shiftName', 'shiftStartTime']),
        displayParentRows() {
            return this.sortColumn === CONSTANTS.FIELDS.LOCATION || this.sortColumn === CONSTANTS.FIELDS.TASK_TYPE;
        },
        displayBlastPacketsAndRings() {
              return this.locations.some(x=>x.miningMethod === MiningMethodType.SubLevelCaving);
        },
        displaySyncTimesButton() {
            return this.dataLoaded && this.editing && this.formData.actualsRows.length > 0;
        },
        filteredAndGroupedActuals() {
            if (this.displayParentRows) {
                return this.filteredActuals;
            }

            return this.filteredActuals.filter(x=>x.hierarchyType !== this.CONSTANTS.ROW_HIERARCHY.PARENT);
        },
        filteredActuals() {
            if(this.showDelays) {
                return this.formData.actualsRows;
            } else {
                const delayTaskTypes = this.taskTypes.filter(tt => tt.isDelay).map(tt => tt.id);

                return this.formData.actualsRows.filter(row => !delayTaskTypes.includes(row.taskTypeId));
            }
        },
        permittedRingsList() {
            return _.flatMap(this.getFlattenedBlastPackets, (bp)=>bp.rings.map(r => ({
                id: r.ringId,
                name: r.ringName,
                locationId: r.locationId,
                blastPacketId: bp.blastPacketId
            })));
        },
        permittedBlastPacketsList() {
            return _.flatMap(this.blastPackets, (bpl)=>bpl.blastPackets.map(bp=>({
                locationId: bpl.id,
                id: bp.id,
                name: RemoveLocationNameFromBlastPacketOrRingName(bp.name),
            })));
        },
        permittedTaskTypeList()
        {
            return this.taskTypeList.filter(tt => !tt.isArchived || this.formData.actualsRows?.some(x => x.taskTypeId === tt.id));
        },
        nonArchivedGroups() {
            if(this.actualsGroups == null)
                return null;

            return this.actualsGroups
                .map(g=>({
                    ...g,
                    definitions: g.definitions.filter(d=>!d.isArchived || this.filteredActuals.some(r=>r.actualsPairs[this.getActualsPairKey(g.groupId, d.id)] !== undefined)),
                    definitionsWithData: g.definitions.filter(d=>this.filteredActuals.some(r=>r.actualsPairs[this.getActualsPairKey(g.groupId, d.id)] !== undefined))
                }))
                .filter(g=>g.definitionsWithData.length > 0 || (g.definitions.length > 0 && !g.isArchived));
        }
    },
    data() {
        return {
            uiLocked: false,
            dataLoaded: false,
            entriesColumnCount: 0,
            formData: {
                actualsRows: []
            },
            actualsGroups: [],
            equipmentList: null,
            locationList: null,
            taskTypeList: null,
            operatorsList: null,
            pageStatus: '',
            saving: false,
            debouncedSaveFn: null,
            formulaDependencies: [],
            totals: {},
            tableKey: 0,  // Add this counter to force the table to re-render when the data changes,
            definitionsIndex: [], // For quick look up of definitions
            shiftIdentifier: '',
            filterOptions: {
                equipments: [],
                locations: [],
                taskTypes: [],
                operators: []
            },
            filterEquipment: null,
            filterLocation: null,
            filterTaskType: null,
            filterOperator: null,
            activeFilteredEquipment: null,
            activeFilteredLocation: null,
            activeFilteredTaskType: null,
            activeFilteredOperator: null,
            filterRefresh: false,
            showDelays: true,
            sortColumn: CONSTANTS.FIELDS.TASK_TYPE,
            sortDirection: CONSTANTS.SORT.UP,
            filterOptionsAll: { key: null, value: 'All' },
            comboUnknownOption: { id: 'unknown', name: 'Unknown' },
            tableHeight: null,
            valueSets: {},
            areReportable: false,
            CONSTANTS: CONSTANTS,
        };
    },
    created() {
        this.debouncedSaveFn = _.debounce(() => {
            if (!this.saving) {
                this.save();
            }
        }, 5000);

        this.filterEquipment = this.filterOptionsAll;
        this.filterLocation = this.filterOptionsAll;
        this.filterTaskType = this.filterOptionsAll;
        this.filterOperator = this.filterOptionsAll;

        this.loadData();
    },
    mounted() {

    },
    methods: {
        groupIsRelevantToTaskType(group, taskTypeId){
            return groupIsRelevantToTaskType(group, taskTypeId);
        },
        toggleMode() {
            this.$emit('custom-event');
        },
        async deleteActuals(row) {
            if (await this.$refs.confirm.openAsDeleteResource(this.$termSync('Actuals Entry'), {})) {
                await Actuals.deleteActuals(this.departmentId, row.id);
                this.removeRowFromActuals(row);
                EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Actuals Entry')} Deleted`);
            }
        },
        removeRowFromActuals(row) {
            const originatingTaskId = row.originatingTaskId;

            this.formData.actualsRows = this.formData.actualsRows.filter(r => r.id !== row.id);

            if(originatingTaskId != null && this.formData.actualsRows.filter(r => r.originatingTaskId === originatingTaskId).length === 0) {
                this.formData.actualsRows = this.formData.actualsRows.filter(r => r.id !== originatingTaskId || r.hierarchyType !== this.CONSTANTS.ROW_HIERARCHY.PARENT);
            } else {
                this.reCalculateParentTotalsForRow(row);
            }
        },
        changeSorting(column) {
            if (this.sortColumn === column) {
                this.sortDirection = this.sortDirection === CONSTANTS.SORT.UP ? CONSTANTS.SORT.DOWN : CONSTANTS.SORT.UP;
            } else {
                this.sortColumn = column;
                this.sortDirection = CONSTANTS.SORT.UP;
            }

            this.sort(this.formData.actualsRows);
        },
        sort(actualsRows) {
            // sort by tasktype and then by location
            let comparator = (a,b) => 0;

            const originatingTaskComparator = (a,b) => {
                const valueAOriginatingTaskId = getRowOriginatingTaskIdForSorting(a);
                const valueBOriginatingTaskId = getRowOriginatingTaskIdForSorting(b);

                if(valueAOriginatingTaskId === valueBOriginatingTaskId)
                    return getHierarchySortOrder(a) - getHierarchySortOrder(b);

                return valueAOriginatingTaskId.localeCompare(valueBOriginatingTaskId);
            };

            const sortMultiplier = this.sortDirection === CONSTANTS.SORT.UP ? 1 : -1;

            if (this.sortColumn === CONSTANTS.FIELDS.EQUIPMENT) {
                comparator = (a,b)=> {
                    let valueA = a.equipment?.name ?? '';
                    let valueB = b.equipment?.name ?? '';

                    return valueA.localeCompare(valueB) * sortMultiplier;
                };
            } else if (this.sortColumn === CONSTANTS.FIELDS.LOCATION) {
                comparator = (a,b)=> {
                    const valueALocation = typeof a.location === 'string' ? a.location : a.location?.name ?? '';
                    const valueATaskType = a.taskType?.name ?? '';
                    const valueBLocation = typeof b.location === 'string' ? b.location : b.location?.name ?? '';
                    const valueBTaskType = b.taskType?.name ?? '';

                    if (valueALocation === valueBLocation) {
                        if (valueATaskType === valueBTaskType)
                            return originatingTaskComparator(a, b);

                        return valueATaskType.localeCompare(valueBTaskType) * sortMultiplier;
                    }

                    return valueALocation.localeCompare(valueBLocation) * sortMultiplier;
                }
            } else if (this.sortColumn === CONSTANTS.FIELDS.TASK_TYPE) {
                comparator = (a,b)=> {
                    let valueA = a.taskType?.name ?? '';
                    let valueB = b.taskType?.name ?? '';


                    if (valueA === valueB)
                        return originatingTaskComparator(a, b);

                    return valueA.localeCompare(valueB) * sortMultiplier;
                };
            } else if (this.sortColumn === CONSTANTS.FIELDS.OPERATOR) {
                comparator = (a,b)=> {
                    let valueA = a.operator?.name ?? '';
                    let valueB = b.operator?.name ?? '';

                    return valueA.localeCompare(valueB) * sortMultiplier;
                }
            }

            actualsRows.sort(comparator);
        },
        getShiftName() {
            return `${this.shiftName} - ${dayjs(this.shiftStartTime).format(DateFormat.WeekDisplay)}`;
        },
        async populateShiftData() {
            this.$wait.start('loading data');

            try {
                await Actuals.populateFromShiftPlan(this.departmentId, this.shiftId);

                EventBus.$emit(Events.ToastSuccess, `Actuals populated from shift plan`);

                // don't manually refresh, because we're about to be told to refresh anyway
                this.$emit('refresh-required');
            } catch (e) {
                Logging.error(e);
                EventBus.$emit(Events.ToastError, `Error populating actuals from shift plan: ${e.message}`);
            } finally {
                this.$wait.end('loading data');
            }
        },
        processFormulasInDefinitions() {
            for (let g = 0; g < this.actualsGroups.length; g++) {
                let currentGroup = this.actualsGroups[g];

                for (let d = 0; d < currentGroup.definitions.length; d++) {
                    let item = currentGroup.definitions[d];

                    if (item.definitionType !== 'Formula' || !item.formula)
                        continue;

                    item.formulaDecimalPlaces = item.decimalPlaces;
                    let lowercaseFormula = item.formula.toLowerCase();
                    item.processedFormula = lowercaseFormula;

                    const regex = /\[(.*?)\]/g;
                    item.isFormulaProcessed = true;

                    for (const match of lowercaseFormula.matchAll(regex)) {
                        let definitionName = match[1];

                        const foundDefinition = currentGroup.definitions.find(i => i.definitionName.toLowerCase() === definitionName.toLowerCase());
                        if (!foundDefinition) {
                            item.isFormulaProcessed = false;
                            break;
                        }

                        let definitionId = foundDefinition.id;

                        // Add to formulaDependencies to know which column to trigger recalculation
                        if (!this.formulaDependencies.includes(this.getActualsPairKey(currentGroup.groupId, definitionId)))
                            this.formulaDependencies.push(this.getActualsPairKey(currentGroup.groupId, definitionId));

                        item.processedFormula = item.processedFormula.replace(`[${definitionName}]`, `[${this.getActualsPairKey(currentGroup.groupId, definitionId)}]`);
                    }

                    if (item.isFormulaProcessed)
                        item.formulaDescription = item.formula;
                    else {
                        Logging.warning('Error processing formula', item.formula);
                        item.formulaDescription = 'Error processing formula: ' + item.formula;
                    }
                }
            }
        },
        async taskTypeChanged() {
            const uniqueTaskTypeIds = [...new Set(
                this.formData.actualsRows
                    .map(row => row.isCustom ? row.taskType?.id : row.taskTypeId)
                    .filter(Boolean) // Remove falsy values
            )];

            this.actualsGroups = await Actuals.getGroups(this.departmentId, uniqueTaskTypeIds);

            this.processFormulasInDefinitions();

            // Trigger table to re-render
            this.tableKey += 1;
        },
        handleInputChange(row, actualsChanged, headerGroup, target) {
            if(row.hierarchyType === this.CONSTANTS.ROW_HIERARCHY.PARENT && target === CONSTANTS.FIELDS.LOCATION)
            {
                this.changeHeaderItemLocation(row);
                return;
            }

            // this should not be possible, but we'll throw it out just in case
            if(row.hierarchyType === this.CONSTANTS.ROW_HIERARCHY.PARENT)
                return;

            switch (target) {
                case CONSTANTS.FIELDS.TASK_TYPE:
                    if(row.originatingTaskId != null)
                        row.originatingTaskId = null;
                    row.taskTypeId = row.taskType.id;
                    this.taskTypeChanged();
                    break;
                case CONSTANTS.FIELDS.OPERATOR:
                    row.operatorId = row.operator.id;
                    break;
                case CONSTANTS.FIELDS.LOCATION:
                    if(row.originatingTaskId != null)
                        row.originatingTaskId = null;
                    row.locationId = row.location.id;
                    if(row.blastPacket != null && !this.getPermittedBlastPacketsForLocation(row.location).some(x=>x.id === row.blastPacket.id))
                        row.blastPacket = null;
                    if(row.ring != null && !this.getPermittedRingsForLocation(row.location, row.blastPacket).some(x=>x.id === row.ring.id))
                        row.ring = null;
                    break;
                case CONSTANTS.FIELDS.BLAST_PACKET:
                    if(row.originatingTaskId != null)
                        row.originatingTaskId = null;
                    row.blastPacketId = row.blastPacket?.id;
                    if(row.ring != null && !this.getPermittedRingsForLocation(row.location, row.blastPacket).some(x=>x.id === row.ring.id))
                        row.ring = null;
                    break;
                case CONSTANTS.FIELDS.EQUIPMENT:
                    row.equipmentId = row.equipment.id;
                    break;
            }

            if (actualsChanged && this.formulaDependencies.includes(`${this.getActualsPairKey(headerGroup.groupId,actualsChanged.id)}`)) {
                this.recalculateFormulaForRow(row);
            }

            this.recalculateTotals();
            if(actualsChanged != null && headerGroup != null)
                this.reCalculateParentTotals(row, null, null); // for now we'll need to update all until we work out how to handle formulae
            else if(target === CONSTANTS.FIELDS.IGNORE_ACTUALS)
                this.reCalculateParentTotalsForRow(row);

            row.status = CONSTANTS.STATUS.NOT_SAVED;
            this.pageStatus = CONSTANTS.STATUS.NOT_SAVED;
            this.debouncedSaveFn();
        },
        changeHeaderItemLocation(row) {
            const affectedRows = this.formData.actualsRows.filter(r => r.originatingTaskId === row.id && r.hierarchyType !== this.CONSTANTS.ROW_HIERARCHY.PARENT);

            affectedRows.forEach(r => {
                r.location = row.location;
                r.locationId = row.location.id;
                r.locationDisplayName = row.location.name;
                r.status = CONSTANTS.STATUS.NOT_SAVED;
            });
            this.pageStatus = CONSTANTS.STATUS.NOT_SAVED;
            this.debouncedSaveFn();
        },
        reCalculateParentTotals(item, updatedGroup, updatedDefinition) {
            if(item.hierarchyType === this.CONSTANTS.ROW_HIERARCHY.INDIV || !item.originatingTaskId)
                return;

            if(updatedDefinition != null && !definitionCanBeSummed(updatedDefinition))
                return;

            let parentRow = this.formData.actualsRows.find(r => r.hierarchyType === this.CONSTANTS.ROW_HIERARCHY.PARENT && r.id === item.originatingTaskId);
            if(parentRow == null)
                return;

            let parentGroup = this.formData.actualsRows.filter(x=>x.originatingTaskId === item.originatingTaskId && x.hierarchyType !== this.CONSTANTS.ROW_HIERARCHY.PARENT);

            if(updatedGroup != null && updatedDefinition != null){
                const pairKey = this.getActualsPairKey(updatedGroup.groupId, updatedDefinition.id);

                const recalculatedTotal = this.calculateParentTotalsForGroup(parentGroup, [{ id: updatedDefinition.id, groupId: updatedGroup.groupId, decimalPlaces: updatedDefinition.decimalPlaces }]);

                parentRow.actualsPairs[pairKey] = recalculatedTotal[pairKey];
            } else {
                parentRow.actualsPairs = this.calculateParentTotalsForGroup(parentGroup);
            }
        },
        reCalculateParentTotalsForRow(item) {
            this.reCalculateParentTotals(item, null, null);
        },
        calculateParentTotalsForGroup(group, targetDefinitions = null) {
            const relevantDefinitions = targetDefinitions ?? this.actualsGroups
                .flatMap(group => group.definitions.map(def => ({ ...def, groupId: group.groupId })))
                .filter(def => definitionCanBeSummed(def));

            let headingResults = {};

            relevantDefinitions.forEach(({ id, groupId, decimalPlaces = 1 }) => {
                const key = this.getActualsPairKey(groupId, id);
                headingResults[key] = group
                    .reduce((sum, row) => {
                        if(!row.ignoreValuesForReporting && row.actualsPairs[key] !== undefined)
                            return sum + +row.actualsPairs[key];
                        else
                            return sum;
                    }, null)
                    ?.toFixed(decimalPlaces);
            });

            return headingResults;
        },
        recalculateTotals() {
            // TODO: Optimize recalculation of totals to limit it to only the changed column (taking into account formula recalculations)

            for (let g = 0; g < this.actualsGroups.length; g++) {
                let currentGroup = this.actualsGroups[g];

                let lowercasedGroupId = currentGroup.groupId.toLowerCase();

                for (let d = 0; d < currentGroup.definitions.length; d++) {
                    let currentDefinition = currentGroup.definitions[d];

                    if (!definitionCanBeSummed(currentDefinition))
                        continue;

                    let cellId = this.getActualsPairKey(lowercasedGroupId, currentDefinition.id);
                    let currentTotal = 0;

                    this.formData.actualsRows.filter(x=>!x.ignoreValuesForReporting && x.hierarchyType !== this.CONSTANTS.ROW_HIERARCHY.PARENT).map(r => {
                        if (r.actualsPairs[cellId] !== undefined)
                            currentTotal += +r.actualsPairs[cellId];
                    });

                    let decimalPlaces = currentDefinition.decimalPlaces ? currentDefinition.decimalPlaces : 1;
                    if (currentDefinition.definitionType === 'Formula' || currentDefinition.definitionType === 'Decimal') {
                        currentTotal = currentTotal.toFixed(decimalPlaces);
                    }

                    this.totals[cellId] = currentTotal;
                }
            }
        },
        recalculateFormulaForRow(row) {
            if(row.hierarchyType === this.CONSTANTS.ROW_HIERARCHY.PARENT)
                return;

            // Have to do it from actualsGroups because actualValues only come back when there are values
            for (let g = 0; g < this.actualsGroups.length; g++) {
                let currentGroup = this.actualsGroups[g];

                // Do not continue with group if not associated with the taskType
                if (!groupIsRelevantToTaskType(currentGroup, row.taskType.id))
                    continue;

                let lowercasedGroupId = currentGroup.groupId.toLowerCase();

                for (let d = 0; d < currentGroup.definitions.length; d++) {
                    let currentDefinition = currentGroup.definitions[d];

                    if (currentDefinition.definitionType === 'Formula' && currentDefinition.isFormulaProcessed) {

                        const regex = /\[(.*?)\]/g;

                        let formulaExpression = currentDefinition.processedFormula;

                        let hasUnmatched = false;
                        for (const match of formulaExpression.matchAll(regex)) {
                            let matchedValue = row.actualsPairs[match[1]];

                            if (matchedValue === undefined || matchedValue === null || matchedValue === '')
                                hasUnmatched = true;

                            formulaExpression = formulaExpression.replace(`[${match[1]}]`, `${matchedValue}`);
                        }

                        // Check that all [x] expressions have been found
                        if (formulaExpression.indexOf('[') < 0 && !hasUnmatched) {
                            let evaluatedResult = eval(formulaExpression).toFixed(currentDefinition.formulaDecimalPlaces);

                            // If we want to remove trailing 0s
                            // evaluatedResult = parseFloat(evaluatedResult);

                            row.actualsPairs[this.getActualsPairKey(lowercasedGroupId, currentDefinition.id)] = evaluatedResult;
                        } else if(hasUnmatched && row.actualsPairs[this.getActualsPairKey(lowercasedGroupId, currentDefinition.id)] != null) {
                            // if we have previously calculated this value but now no longer can, null it out
                            row.actualsPairs[this.getActualsPairKey(lowercasedGroupId, currentDefinition.id)] = null;
                        }
                    }
                }
            }
        },
        async save() {
            if (this.pageStatus === CONSTANTS.STATUS.ERROR)
                return false;

            let saveError = false;

            // Process and save all actuals rows that have been modified
            let updateCommands = [];
            let rowsToSave = this.formData.actualsRows.filter(i => i.status === CONSTANTS.STATUS.NOT_SAVED && i.taskType);

            rowsToSave.map(rowToSave => {
                let updateCommand = {
                    id: rowToSave.id,
                    equipmentId: rowToSave.equipment ? rowToSave.equipment.id : null,
                    operatorId: rowToSave.operator ? rowToSave.operator.id : null,
                    taskTypeId: rowToSave.taskType ? rowToSave.taskType.id : null,
                    taskTypeDisplayName: rowToSave.taskTypeDisplayName,
                    blastPacketId: rowToSave.blastPacket ? rowToSave.blastPacket.id : null,
                    locationRingId: rowToSave.ring ? rowToSave.ring.id : null,
                    comment: rowToSave.comment,
                    startTime: rowToSave.startTime,
                    endTime: rowToSave.endTime,
                    isCustom: rowToSave.isCustom,
                    originatingTaskId: rowToSave.originatingTaskId,
                    ignoreValuesForReporting: rowToSave.ignoreValuesForReporting,
                    actualsValues: []
                };

                // Check if location is a custom string, otherwise set to what is in combobox
                if (rowToSave.location && typeof rowToSave.location === 'string') {
                    updateCommand.locationId = null;
                    updateCommand.locationDisplayName = rowToSave.location;
                } else {
                    updateCommand.locationDisplayName = null;
                    updateCommand.locationId = rowToSave.location ? rowToSave.location.id : null;
                }

                // Set any combo box 'unknown' values to unknown flag
                if (updateCommand.equipmentId === 'unknown') {
                    updateCommand.equipmentId = null;
                    updateCommand.isEquipmentUnknown = true;
                }
                if (updateCommand.locationId === 'unknown') {
                    updateCommand.locationId = null;
                    updateCommand.isLocationUnknown = true;
                }
                if (updateCommand.operatorId === 'unknown') {
                    updateCommand.operatorId = null;
                    updateCommand.isOperatorUnknown = true;
                }

                for (let g = 0; g < this.actualsGroups.length; g++) {
                    let currentGroup = this.actualsGroups[g];

                    if (groupIsRelevantToTaskType(currentGroup, rowToSave.taskType.id)) {
                        let lowercasedGroupId = currentGroup.groupId.toLowerCase();

                        for (let d = 0; d < currentGroup.definitions.length; d++) {
                            let currentDefinition = currentGroup.definitions[d];

                            // Do not add if there are no recorded values
                            if (!rowToSave.actualsPairs[this.getActualsPairKey(lowercasedGroupId, currentDefinition.id)])
                                continue;

                            let defValue = {
                                actualsDefinitionId: currentDefinition.id,
                                actualsGroupId: lowercasedGroupId,
                                value: rowToSave.actualsPairs[this.getActualsPairKey(lowercasedGroupId, currentDefinition.id)],
                                type: this.getTypeStringAsNumber(currentDefinition.definitionType)
                            };

                            updateCommand.actualsValues.push(defValue);
                        }
                    }
                }

                updateCommands.push(updateCommand);
            });

            if (updateCommands.length > 0) {
                this.uiLocked = true;
                this.saving = true;
                this.pageStatus = CONSTANTS.STATUS.SAVING;

                try {
                    await Actuals.update(this.departmentId, this.shiftId, updateCommands);
                } catch (e) {
                    saveError = true;
                    Logging.error(e);

                    rowsToSave.map(i => {
                        i.status = CONSTANTS.STATUS.ERROR;
                    });
                } finally {
                    this.saving = false;
                    this.uiLocked = false;

                    if (!saveError) {
                        this.pageStatus = CONSTANTS.STATUS.SAVED;

                        rowsToSave.map(i => {
                            i.status = '';
                        });
                    } else
                        this.pageStatus = CONSTANTS.STATUS.ERROR;
                }
            }

            return !saveError;
        },
        async cancelReportability() {
            if(await this.confirmCancelReportability()) {
                await this.markAsNotReportable();
            }
        },
        async markReportable() {
            if(await this.confirmReadyToReport()) {
                await this.markAsReportable();
            }
        },
        async confirmCancelReportability() {
            // @ts-ignore
            return await this.$refs.confirm.open(
                'Cancel Reportability',
                'Are you sure you want to mark these plods as not ready for reporting?',
                { color: 'red' }
            );
        },
        async confirmReadyToReport(){
            // @ts-ignore
            return await this.$refs.confirm.multilineOpen(
                'Mark Reportable',
                ['Are you sure you want to mark these plods are ready for reporting?','You will still be able to make changes after doing so.'],
                { color: 'orange' }
            );
        },
        async markAsReportable() {
            if (this.pageStatus === CONSTANTS.STATUS.ERROR)
                return false;

            this.$wait.start('marking-reportable');
            try {
                await Actuals.markActualsAsApproved(this.departmentId, this.shiftId);
                this.areReportable = true;
                EventBus.$emit(Events.ToastSuccess, 'Marked reportable');
            } catch (error) {
                Logging.error(error);
                EventBus.$emit(Events.ToastError, `Error marking actuals as ready for reporting: ${error.message}`);
            } finally {
                this.$wait.end('marking-reportable');
            }
        },
        async markAsNotReportable() {
            if (this.pageStatus === CONSTANTS.STATUS.ERROR)
                return false;

            this.$wait.start('marking-not-reportable');
            try {
                await Actuals.markActualsAsNotApproved(this.departmentId, this.shiftId);
                this.areReportable = false;
                EventBus.$emit(Events.ToastWarning, 'Marked not reportable');
            } catch (error) {
                Logging.error(error);
                EventBus.$emit(Events.ToastError, `Error marking actuals as not ready for reporting: ${error.message}`);
            } finally {
                this.$wait.end('marking-not-reportable');
            }
        },
        getTypeStringAsNumber(typeString) {
            switch (typeString) {
                case 'Integer':
                    return 0;
                case 'Decimal':
                    return 1;
                case 'Time':
                    return 2;
                case 'Text':
                    return 3;
                case 'Formula':
                    return 4;
                case 'Boolean':
                    return 5;
                case 'Date':
                    return 6;
            }
        },
        validateInput(event, dataType) {
            let newValue = event.target.value + event.key;

            let validationRegex = null;

            if (dataType === 'Integer')
                validationRegex = /^-?\d+$/;
            else if (dataType === 'Decimal')
                validationRegex = /^-?\d*\.?\d*$/;

            if (validationRegex && !validationRegex.test(newValue)) {
                event.preventDefault();
            }
        },
        switchToEdit() {
            this.editing = true;
            this.uiLocked = false;
            this.pageStatus = '';
        },
        finishEditing() {
            if (this.pageStatus === CONSTANTS.STATUS.NOT_SAVED) {
                if (this.debouncedSaveFn)
                    this.debouncedSaveFn.cancel();

                this.save(true);
            } else {
                this.editing = false;
                this.uiLocked = true;
            }
        },
        async syncTimes() {
            if(await this.confirmSyncTimes()){
                this.$wait.start('syncing-times');
                try {
                    await Actuals.syncTimesFromShiftPlan(this.departmentId, this.shiftId);
                    EventBus.$emit(Events.ToastSuccess, 'Task times copied from shift plan');
                    // don't manually refresh, because we're about to be told to refresh anyway
                    this.$emit('refresh-required');
                } catch (error) {
                    Logging.error(error);
                    EventBus.$emit(Events.ToastError, `Error copying task times from shift plan: ${error.message}`);
                } finally {
                    this.$wait.end('syncing-times');
                }
            }
        },
        async confirmSyncTimes(){
            // @ts-ignore
            return await this.$refs.confirm.multilineOpen(
                'Copy Task Times',
                ['Are you sure you want to copy task times from the shift plan?','You will still be able to make changes after doing so.'],
                { color: 'orange' }
            );
        },
        addRow() {
            let newItem = {
                id: uuidv4(),
                actualsPairs: {},
                status: CONSTANTS.STATUS.NOT_SAVED,
                isCustom: true,
                equipment: undefined,
                location: undefined,
                operator: undefined,
                taskType: undefined,
                hierarchyType: this.CONSTANTS.ROW_HIERARCHY.INDIV,
            };

            // Set item default values that are uneditable, if filter has been applied

            if (this.filterEquipment)
                newItem.equipment = this.equipmentList.find(i => i.id === this.filterEquipment?.key?.toLowerCase());

            if (this.filterLocation)
                newItem.location = this.locationList.find(i => i.id === this.filterLocation?.key?.toLowerCase());

            if (this.filterOperator)
                newItem.operator = this.operatorsList.find(i => i.id === this.filterOperator?.key?.toLowerCase());

            if (this.filterTaskType)
                newItem.taskType = this.taskTypeList.find(i => i.id === this.filterTaskType?.key?.toLowerCase());

            this.formData.actualsRows.push(newItem);
        },
        addChildRow(parentRow){
            let newItem = {
                id: uuidv4(),
                actualsPairs: {},
                status: CONSTANTS.STATUS.NOT_SAVED,
                isCustom: false,
                equipment: undefined,
                location: parentRow.location,
                operator: undefined,
                taskType: parentRow.taskType,
                originatingTaskId: parentRow.id,
                hierarchyType: this.CONSTANTS.ROW_HIERARCHY.CHILD,
            };

            // Set item default values that are uneditable, if filter has been applied

            if (this.filterEquipment)
                newItem.equipment = this.equipmentList.find(i => i.id === this.filterEquipment?.key?.toLowerCase());

            if (this.filterOperator)
                newItem.operator = this.operatorsList.find(i => i.id === this.filterOperator?.key?.toLowerCase());

            const parentRowIndex = this.formData.actualsRows.findIndex(r => r.id === parentRow.id);

            // splice in the new item just after the parent
            this.formData.actualsRows.splice(parentRowIndex + 1, 0, newItem);
        },
        clearFilter() {
            this.filterEquipment = this.filterOptionsAll;
            this.filterLocation = this.filterOptionsAll;
            this.filterTaskType = this.filterOptionsAll;
            this.filterOperator = this.filterOptionsAll;

            this.activeFilteredEquipment = null;
            this.activeFilteredLocation = null;
            this.activeFilteredTaskType = null;
            this.activeFilteredOperator = null;

            this.loadData(true);
        },
        async loadData(filterRefresh = false) {
            if (!this.csvExportDateRangeStart && this.shiftStartTime)
                this.csvExportDateRangeStart = this.shiftStartTime.format('YYYY-MM-DD');

            if (!this.csvExportDateRangeEnd && this.shiftStartTime)
                this.csvExportDateRangeEnd = this.shiftStartTime.format('YYYY-MM-DD');

            this.filterRefresh = filterRefresh;

            this.shiftIdentifier = this.getShiftName();

            this.equipmentList = [this.comboUnknownOption, ...await Equipment.get(['EquipmentRole', 'Departments'])];
            this.locationList = [this.comboUnknownOption, ...this.locations];
            this.taskTypeList = this.taskTypes;
            this.operatorsList = [this.comboUnknownOption, ...this.staffMembers];

            const options = await Actuals.getFilterOptionsForDepartmentShiftId(this.departmentId, this.shiftId);

            // only go and get blast packets if we're dealing with sublevel caving, otherwise it's a waste
            if(this.displayBlastPacketsAndRings)
                await useBlastPacketsStore().populateStoreForDepartment(this.$route.params.dept);

            this.filterOptions.locations = [this.filterOptionsAll, ...this.locations.map(l => {
                return { key: l.id, value: l.name };
            })];
            this.filterOptions.equipments = [this.filterOptionsAll, ...options.equipments];
            this.filterOptions.taskTypes = [this.filterOptionsAll, ...options.taskTypes];
            this.filterOptions.operators = [this.filterOptionsAll, ...options.operators];

            const actualsResponse = await Actuals.getActualsDataForDepartmentShiftId(
                this.departmentId,
                this.shiftId,
                this.filterTaskType?.key,
                this.filterOperator?.key,
                this.filterEquipment?.key,
                this.filterLocation?.key
            );
            let actualsRows = actualsResponse.actualsResponses;
            this.areReportable = actualsResponse.markedAsReportable;

            // Get unique taskTypeIds from actuals data
            const uniqueTaskTypeIds = Array.from(
                new Set(actualsRows.flatMap(a => a.taskTypeId))
            );

            // Get groups
            if (uniqueTaskTypeIds && uniqueTaskTypeIds.length > 0) {
                this.actualsGroups = await Actuals.getGroups(this.departmentId, uniqueTaskTypeIds);

                // Add empty value as option for value sets
                this.actualsGroups?.map(g => {
                    g.definitions?.map(d => {
                        if (d.valueSet && d.valueSet.length >= 1) {
                            this.valueSets[d.id] = ['', ...d.valueSet];
                        }
                    });
                });
            }

            this.processFormulasInDefinitions();

            const grouped = _.groupBy(actualsRows, 'originatingTaskId');

            actualsRows = Object.entries(grouped).flatMap(([key, group]) => {
                group.map(row => {
                    // 1. Change time format to HH:mm
                    row.actualValues.map(item => {
                        if (item.type === 'Time' && item.value) {
                            // Convert to HH:mm
                            let date = new Date(item.value);
                            item.value = date.toISOString().substr(11, 5);
                        }

                        if(item.type === 'Date' && item.value) {
                            // Convert to YYYY-MM-DD
                            let date = new Date(item.value);
                            item.value = date.toISOString().substr(0, 10);
                        }

                        if (item.value && (item.type === 'Decimal' || item.type === 'Formula') && item.decimalPlaces)
                            item.value = item.value.toFixed(item.decimalPlaces);
                        else if (item.type === 'Integer')
                            item.value = item.value.toString();
                    });

                    // 2. Reduce actuals data to a key value pair, with ID being a concatenation of groupId and defId
                    row.actualsPairs = row.actualValues.reduce((acc, item) => {
                        acc[this.getActualsPairKey(item.groupId, item.actualValueId)] = item.value;

                        return acc;
                    }, {});

                    // 3. Massage equipment, location, taskType and operator into objects needed for combo boxes
                    if (row.isEquipmentUnknown)
                        row.equipment = this.comboUnknownOption;
                    else
                        row.equipment = {
                            id: row.equipmentId,
                            name: row.equipmentName
                        };

                    row.taskType = {
                        id: row.taskTypeId,
                        name: row.taskTypeName
                    };

                    row.blastPacket = row.blastPacketId != null ? {
                        id: row.blastPacketId,
                        name: row.blastPacketName
                    } : null;

                    row.ring = row.locationRingId != null ? {
                        id: row.locationRingId,
                        name: row.locationRingName
                    } : null;


                    if (row.isLocationUnknown)
                        row.location = this.comboUnknownOption;
                    else if (!row.locationId && row.locationDisplayName)
                        row.location = row.locationDisplayName;
                    else
                        row.location = {
                            id: row.locationId,
                            name: row.locationName
                        };

                    if (row.isOperatorUnknown)
                        row.operator = this.comboUnknownOption;
                    else
                        row.operator = {
                            id: row.operatorId,
                            name: row.operatorName
                        };
                });

                if(group.length === 1 || key == null || key == 'null')
                    return group.map(g=>({
                        ...g,
                        hierarchyType: this.CONSTANTS.ROW_HIERARCHY.INDIV,
                    }));

                const [firstRow] = group;
                const { taskType, location } = firstRow || {};

                const headingItem = {
                    id: key,
                    taskType,
                    location,
                    actualsPairs: {},
                    hierarchyType: this.CONSTANTS.ROW_HIERARCHY.PARENT,
                };

                if (taskType) {
                    const relevantDefinitions = this.actualsGroups
                        .filter(g=> groupIsRelevantToTaskType(g, taskType.id))
                        .flatMap(group => group.definitions.map(def => ({ ...def, groupId: group.groupId })))
                        .filter(def => definitionCanBeSummed(def));

                    headingItem.actualsPairs = this.calculateParentTotalsForGroup(group, relevantDefinitions);
                }

                return [headingItem, ...group.map(g=>({
                    ...g,
                    hierarchyType: this.CONSTANTS.ROW_HIERARCHY.CHILD,
                }))];
            });

            // Once mounted, massage data

            this.sort(actualsRows);

            this.formData.actualsRows = actualsRows;

            // Calculate formula for each row
            this.formData.actualsRows.map(row => {
                this.recalculateFormulaForRow(row);
            });

            // Calculate totals
            this.recalculateTotals();

            if (this.filterEquipment.key)
                this.activeFilteredEquipment = this.filterEquipment;
            if (this.filterLocation.key)
                this.activeFilteredLocation = this.filterLocation;
            if (this.filterTaskType.key)
                this.activeFilteredTaskType = this.filterTaskType;
            if (this.filterOperator.key)
                this.activeFilteredOperator = this.filterOperator;

            this.dataLoaded = true;
            this.filterRefresh = false;
        },
        getPermittedRingsForLocation(location, blastPacket) {
            if (!this.displayBlastPacketsAndRings)
                return this.permittedRingsList;

            if(location == null)
                return [];

            return this.permittedRingsList.filter(x=>x.locationId === location.id && (blastPacket?.id == null || blastPacket.id === x.blastPacketId)).map(x=>({
                ...x,
                name: RemoveLocationNameFromBlastPacketOrRingName(x.name, location.name)
            }));
        },
        getPermittedBlastPacketsForLocation(location) {
            if (!this.displayBlastPacketsAndRings)
                return this.permittedBlastPacketsList;

            if(location == null)
                return [];

            return this.permittedBlastPacketsList.filter(x=>x.locationId === location.id).map(x=>({
                ...x,
                name: RemoveLocationNameFromBlastPacketOrRingName(x.name, location.name)
            }));
        },
        getTextFieldType(type) {
            if (type === 'Time')
                return 'time';
            else if(type === 'Date')
                return 'date';
            else
                return 'text';
        },
        getActualsPairKey(groupId, defId) {
            return `${groupId.toLowerCase()}_${defId.toLowerCase()}`;
        }
    },
    watch: {
        async refreshRequiredToggle(newValue, oldValue) {
            if (newValue !== oldValue) {
                await this.loadData(true);
            }
        }
    }
});
</script>


<style scoped>
.totals-row > td {
    border-top: 3px SOLID #EEE !important;
}

.edit-mode .theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
    color: #FFF !important;
}

.theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr:last-child > th {
    border-bottom: 1px SOLID #FFF;
}

.col-boolean {
    min-width: 50px;
}

.col-decimal {
    min-width: 70px;
}

.col-integer {
    min-width: 70px;
}

.col-formula {
    min-width: 70px;
}

.col-text {
    min-width: 100px;
}

.col-time {
    min-width: 120px;
}

.grey-box {
    background-color: #EEE;
}

.formula-indicator {
    color: darkorange;
    font-style: italic;
}

.header-s {
    font-size: 12px;
}

.header-s .edit-mode {
    background-color: #15A69A;
}

.header-s .read-mode {
    background-color: #DDD;
}

.custom-plod-table th,
.custom-plod-table td {
    border: 1px solid #FFF;
    padding: 8px 5px !important;
    height: auto !important;
    text-align: center !important;
}

.custom-plod-table {
    & table {
        /*
        Note: collapse doesn't quite work at this point because the table border gets rendered behind the cells and shifts.
        border-collapse: collapse;
        */
    }
}

.custom-plod-table {
    & td {
        border: 0 solid #FFF;
        padding: 2px 5px 2px 5px !important;
        text-align: center;
    }
}

.fixed-width-combo {
    max-width: 140px;
}

.screen-x .fixed-width-combo, .screen-l .fixed-width-combo {
    max-width: 110px;
}

.extra-wide {
    min-width: 150px;
    max-width: 150px;
}

.screen-x .extra-wide, .screen-l .extra-wide {
    min-width: 120px;
    max-width: 120px;
}

.actions-column {
    min-width: 50px;
}

.indicator-column {
    min-width: 50px;
}

.group-name {
    font-style: italic;
}

.add-new-row-button {
    margin-left: 10px;
}

.header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.table_wrapper {
    overflow: auto;
    max-width: 100%;

    position: fixed;
    top: 244px;
    bottom: 0;
    left: 0;
    right: 0;
}

.high-level-actuals {
    top: 385px;
}

.v-input--is-disabled {
    opacity: 0.4;
}

.lock-icon {
    color: #CCC;
}

.status-chips {
    font-weight: bold;
}

.sortable-field {
    cursor: pointer;
}

.filter-button {
    width: 90px;
}

.clear-filter {
    min-width: 40px !important;
    width: 40px;
}

.filter-options {
    width: 170px;
}

input[readonly] {
    font-style: italic;
    color: #777;
}

tbody tr:hover {
    background-color: transparent !important;
}

/* 
Make table headers sticky
*/

/* Targets main 6 stick headers */
th.header:nth-child(-n+6) {
    position: sticky;
    z-index: 99;
    top: 0;
}

/* Targets all other top row header cells */
th:nth-child(n+7):not(.subheader) {
    position: sticky;
    z-index: 90;
    top: 0;
}

/* Targets all subheader cells */
th.subheader {
    position: sticky;
    z-index: 90;
    top: 35px;
}

.edit-mode th.header:nth-child(-n+6),
.edit-mode th.subheader,
.edit-mode th:nth-child(n+7):not(.subheader) {
    background-color: var(--v-primary-base);
}

.read-mode th.header:nth-child(-n+6),
.read-mode th.subheader,
.read-mode th:nth-child(n+7):not(.subheader) {
    background-color: #D1D1D1;
}

/* Targets the first 5 columns of the body of table */
td:nth-child(-n+6) {
    position: sticky;
    background-color: #FFF; /* Ensure the background isn't transparent */
    z-index: 10; /* Ensure sticky columns appear above other cells */
}

.grouped-row-child td {
    background-color: #F9F9F9;
}

/* Set the left position for each sticky column */
th:nth-child(1),
td:nth-child(1) {
    left: 0;
}

th:nth-child(2),
td:nth-child(2) {
    left: 50px;
}

th:nth-child(3),
td:nth-child(3) {
    left: 100px;
}

th:nth-child(4),
td:nth-child(4) {
    left: 250px;
}

.screen-l th:nth-child(4),
.screen-l td:nth-child(4),
.screen-x th:nth-child(4),
.screen-x td:nth-child(4) {
    left: 220px;
}

th:nth-child(5),
td:nth-child(5) {
    left: 400px;
}

.screen-l th:nth-child(5),
.screen-l td:nth-child(5),
.screen-x th:nth-child(5),
.screen-x td:nth-child(5) {
    left: 340px;
}

th:nth-child(6),
td:nth-child(6) {
    left: 550px;
}

.screen-l th:nth-child(6),
.screen-l td:nth-child(6),
.screen-x th:nth-child(6),
.screen-x td:nth-child(6) {
    left: 460px;
}

</style>